import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { useMemo } from 'react'

import {
  ArtistWithSubscriptionFragment,
  Maybe,
  MembershipInterval,
  MembershipPlanRelationship,
  MembershipSubscriptionPlanDetailFragment,
  MembershipSubscriptionPlanStatus,
  MembershipSubscriptionProductDetailFragment,
  PatronType,
} from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { MembershipSubscriptionProduct } from '@/core/membership'

export type ProductBoxProps = {
  product: MembershipSubscriptionProductDetailFragment
  activeProduct?: MembershipSubscriptionProduct
  downgradeProduct?: MembershipSubscriptionProduct
  upgradeProduct?: MembershipSubscriptionProduct
  onClickButton: (
    event: unknown,
    plan: MembershipSubscriptionPlanDetailFragment
  ) => void
  loading: boolean
  expiredAt: Maybe<string>
  patronType: Maybe<PatronType>
  store: Maybe<string>
  pendingPurchasePatron: Maybe<
    ArtistWithSubscriptionFragment['pendingPurchasePatron']
  >
  enableMembershipCarrierPayment: boolean
}

export const ProductBox: Organism<ProductBoxProps> = ({
  product,
  activeProduct,
  downgradeProduct,
  upgradeProduct,
  loading,
  onClickButton,
  expiredAt,
  patronType,
  store,
  pendingPurchasePatron,
  enableMembershipCarrierPayment,
}) => {
  const { t } = useTranslation('artistMembership')
  const { onClick } = useBrowserLink(pendingPurchasePatron?.voucherUrl ?? '')
  const isActiveProduct = useMemo(
    () => activeProduct?.productId === product.productId,
    [activeProduct?.productId, product.productId]
  )

  const plan = useMemo(
    () =>
      product.plans.find(
        plan =>
          plan.interval ===
          (activeProduct?.plan.interval ?? MembershipInterval.Month)
      ),
    [activeProduct?.plan.interval, product.plans]
  )

  const { monthlyPlan, yearlyPlan } = useMemo(() => {
    const monthlyPlan = product?.plans?.find(
      plan =>
        plan.interval === MembershipInterval.Month &&
        plan.status === MembershipSubscriptionPlanStatus.Active
    )
    const yearlyPlan = product?.plans?.find(
      plan =>
        plan.interval === MembershipInterval.Year &&
        plan.status === MembershipSubscriptionPlanStatus.Active
    )
    return { monthlyPlan, yearlyPlan }
  }, [product?.plans])

  const lowestAmount = useMemo(() => {
    const amounts = [
      monthlyPlan?.amount ?? 0,
      (yearlyPlan?.amount ?? 0) / 12,
    ].filter(amount => amount > 0)
    return Math.min(...amounts) || 0
  }, [monthlyPlan, yearlyPlan])

  /**
   * ボタンのテキスト表示と挙動
   */
  const { buttonText, disabled, color, button } = useMemo<{
    buttonText?: string
    disabled?: boolean
    color?: 'inherit' | 'primary'
    button: boolean
  }>(() => {
    switch (plan?.planRelationship) {
      case 'forLeave':
        return {
          buttonText: t('cancelChange'),
          disabled: false,
          color: 'inherit',
          button: activeProduct?.productId === downgradeProduct?.productId,
        }
      /**
       * carrier_forLeave
       * キャリア決済のプラン切り替えで離脱予定の現在のプラン
       * アーティスト側でキャリア決済オプションが OFF になった場合
       * 解約申請はできるが、解約申請のキャンセルは行えない
       * ref. https://app.clickup.com/t/908127/UT-20671
       */
      case 'carrier_forLeave':
        return {
          buttonText: t('cancelChange'),
          disabled: !enableMembershipCarrierPayment,
          color: 'inherit',
          button: activeProduct?.productId === downgradeProduct?.productId,
        }
      case 'forJoinedUpper':
      case 'forJoinedLower':
        return {
          buttonText: t('cancelChange'),
          disabled: false,
          color: 'inherit',
          button: true,
        }
      /**
       * carrier_forJoinedUpper
       * キャリア決済のプラン切り替えで加入予定の今より上位のプラン
       * アーティスト側でキャリア決済オプションが OFF になった場合
       * アップグレード申請中のアップグレード申請キャンセルは行えない
       * ref. https://app.clickup.com/t/908127/UT-20671
       *
       * carrier_forJoinedLower
       * キャリア決済のプラン切り替えで加入予定の今より下位のプラン
       * アーティスト側でキャリア決済オプションが OFF になった場合
       * ダウングレード申請中のダウングレード申請キャンセルは行えない
       * ref. https://app.clickup.com/t/908127/UT-20671
       */
      case 'carrier_forJoinedUpper':
      case 'carrier_forJoinedLower':
        return {
          buttonText: t('cancelChange'),
          disabled: !enableMembershipCarrierPayment,
          color: 'inherit',
          button: true,
        }
      case 'stopping':
      case 'joined':
      case 'konbini_joined':
      case 'konbini_waitingPayment':
        return {
          button: false,
        }
      case 'notDowngradingUpper':
      case 'carrier_notUpgradingUpper':
      case 'konbini_notDowngradingUpper':
      case 'stoppingUpper':
        return {
          buttonText: t('upgrade'),
          disabled: true,
          color: 'primary',
          button: true,
        }
      case 'upper':
      case 'konbini_upper':
        return {
          buttonText: t('upgrade'),
          disabled: false,
          color: 'primary',
          button: true,
        }
      /**
       * carrier_upper
       * キャリア決済で加入中の現在のプランより上位のプラン
       * アーティスト側でキャリア決済オプションが OFF になった場合
       * 新規でのアップグレード申請は行えない
       * ref. https://app.clickup.com/t/908127/UT-20671
       */
      case 'carrier_upper':
        return {
          buttonText: t('upgrade'),
          disabled: !enableMembershipCarrierPayment,
          color: 'primary',
          button: true,
        }
      case 'notDowngradingLower':
      case 'konbini_notDowngradingLower':
      case 'carrier_notUpgradingLower':
      case 'stoppingLower':
        return {
          buttonText: t('downgrade'),
          disabled: true,
          color: 'primary',
          button: true,
        }
      case 'lower':
      case 'konbini_lower':
        return {
          buttonText: t('downgrade'),
          disabled: false,
          color: 'primary',
          button: true,
        }
      /**
       * carrier_lower
       * キャリア決済で加入中の現在のプランより下位のプラン
       * アーティスト側でキャリア決済オプションが OFF になった場合
       * 新規でのダウングレード申請は行えない
       * ref. https://app.clickup.com/t/908127/UT-20671
       */
      case 'carrier_lower':
        return {
          buttonText: t('downgrade'),
          disabled: !enableMembershipCarrierPayment,
          color: 'primary',
          button: true,
        }
      default:
        return {
          buttonText: t('selectPlan'),
          disabled: false,
          color: 'primary',
          button: true,
        }
    }
  }, [
    activeProduct?.productId,
    downgradeProduct?.productId,
    plan?.planRelationship,
    t,
  ])

  const textNote = useMemo(() => {
    switch (plan?.planRelationship) {
      case 'forLeave':
        if (downgradeProduct?.productId === product.productId) {
          return t('changeNextPlanNote', {
            expiredAt: DateTime.formatDate(expiredAt),
          })
        }
        if (
          downgradeProduct?.plan.amount &&
          activeProduct?.plan.amount &&
          downgradeProduct?.plan.amount > activeProduct?.plan.amount
        ) {
          return t('forLeaveUpgradeNote', {
            expiredAt: DateTime.formatDate(expiredAt),
            title: downgradeProduct?.title,
          })
        }
        if (
          downgradeProduct?.plan.amount &&
          activeProduct?.plan.amount &&
          downgradeProduct?.plan.amount < activeProduct?.plan.amount
        ) {
          return t('forLeaveDowngradeNote', {
            expiredAt: DateTime.formatDate(expiredAt),
            title: downgradeProduct?.title,
          })
        }
        return null
      // キャリア決済を行なっている離脱予定の現在のプラン
      case 'carrier_forLeave':
        // アップグレード予定の場合
        if (upgradeProduct)
          return t('carrierForLeaveNote', {
            expiredAt: DateTime.formatDate(expiredAt),
            title: upgradeProduct?.title,
          })
        // ダウングレード予定の場合
        if (downgradeProduct)
          return t('carrierForLeaveNote', {
            expiredAt: DateTime.formatDate(expiredAt),
            title: downgradeProduct?.title,
          })
        return null
      /**
       * carrier_notUpgradingLower
       * キャリア決済のアップグレードで加入予定でない今より下位のプラン
       * 中位プランから最上位プランにアップグレードする際の最下位プランが該当する
       */
      case 'notDowngradingLower':
      case 'carrier_notUpgradingLower':
      case 'stoppingLower':
      case 'konbini_notDowngradingLower':
        return t('notDowngradePlanNote')
      /**
       * carrier_notUpgradingUpper
       * キャリア決済のアップグレードで加入予定でない今より上位のプラン
       * 最下位プランから中位プランにアップグレードする際の最上位プランが該当する
       */
      case 'notDowngradingUpper':
      case 'stoppingUpper':
      case 'carrier_notUpgradingUpper':
      case 'konbini_notDowngradingUpper':
        return t('notUpgradePlanNote')
      case 'forJoinedLower':
      case 'carrier_forJoinedLower':
        return t('changeDowngradeNextPlanNote', {
          expiredAt: DateTime.formatDate(expiredAt),
        })
      /**
       * carrier_forJoinedUpper
       * キャリア決済のプラン切り替えで加入予定の今より上位のプラン
       */
      case 'forJoinedUpper':
      case 'carrier_forJoinedUpper':
        return t('changeUpgradeNextPlanNote', {
          expiredAt: DateTime.formatDate(expiredAt),
        })
      case 'konbini_waitingPayment':
        return t('konbiniJoinedNote')
      default:
        return null
    }
  }, [
    activeProduct?.plan.amount,
    downgradeProduct?.plan.amount,
    downgradeProduct?.productId,
    downgradeProduct?.title,
    expiredAt,
    plan?.planRelationship,
    product.productId,
    t,
  ])

  if (!plan) {
    return null
  }

  return (
    <OrganismFrame>
      <Card elevation={0} sx={{ borderRadius: 2, bgcolor: 'grey.100' }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack spacing={4} sx={{ pb: 5 }}>
            <Typography variant="h5" sx={styleSets.lineCamp(2)}>
              {product.title}
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack spacing={1}>
                {isActiveProduct ? (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="subtitle1">
                      {t(`${activeProduct?.plan.interval}Fee`)}
                    </Typography>
                    <Typography variant="body2">
                      {t('amount', {
                        amount: activeProduct?.plan.formattedAmount,
                      })}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="subtitle1">
                    {yearlyPlan && monthlyPlan
                      ? t('yearlyPlanNote', {
                          amountPerYear: yearlyPlan?.formattedAmount,
                          amountPerMonth: monthlyPlan?.formattedAmount,
                        })
                      : monthlyPlan
                      ? t('monthlyAmount', {
                          amount: numeral(lowestAmount).format('0,0'),
                        })
                      : t('yearlyAmount', {
                          amount: numeral(lowestAmount).format('0,0'),
                        })}
                  </Typography>
                )}
              </Stack>
              <PlanLabel
                planRelationship={plan.planRelationship || 'unjoined'}
                interval={plan?.interval}
              />
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: 'grey.200' }} />
          <Stack spacing={5} sx={{ py: 5 }}>
            <Typography variant="body2">{product.content}</Typography>
            {!!product.annualContent && (
              <Stack>
                <Typography variant="body2" fontWeight={600}>
                  {t('annualContentTitle')}
                </Typography>
                <Typography variant="body2">{product.annualContent}</Typography>
              </Stack>
            )}
          </Stack>
          {(button || textNote) && <Divider sx={{ borderColor: 'grey.200' }} />}
          {!!textNote && (
            <Typography variant="overline" color="text.secondary">
              {textNote}
            </Typography>
          )}
          {button && (
            <Stack flex={1} alignItems="center" sx={{ pt: 5 }}>
              {/**
               * @description
               * 決済方法によって振る舞いを変える
               * stripe: stripe経由でキャンセル
               * escott: 外部キャリア決済画面にて追加手続き
               * iap: ios設定から解約を促す
               * google pay: アプリストアから解約を促す
               */}
              {(!patronType ||
                patronType === 'stripe' ||
                patronType === 'escott') && (
                <Button
                  size="large"
                  color={color}
                  sx={{
                    width: 240,
                    bgcolor: color === 'inherit' ? 'grey.50' : undefined,
                    '&.Mui-disabled': { bgcolor: 'grey.50' },
                  }}
                  loading={loading}
                  disabled={disabled}
                  onClick={event => plan && onClickButton(event, plan)}
                >
                  {buttonText}
                </Button>
              )}
              {store === 'app_store' && (
                <Typography variant="body2">{t('cancelAtIOS')}</Typography>
              )}
              {store === 'play_store' && (
                <Typography variant="body2">{t('cancelAtAndroid')}</Typography>
              )}
            </Stack>
          )}
          {plan.planRelationship === 'konbini_waitingPayment' && (
            <Stack alignItems="center" pt={5}>
              <Stack direction="row" spacing={1}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('paymentDeadlineKonbini')}
                </Typography>
                <Typography variant="subtitle1">
                  {pendingPurchasePatron?.formattedExpiredAt}
                </Typography>
              </Stack>
              <Button variant="text" onClick={onClick}>
                {t('confirmPayment')}
              </Button>
            </Stack>
          )}
          {plan.planRelationship === 'stopping' && (
            <>
              <Divider sx={{ borderColor: 'grey.200' }} />
              <Stack flex={1} sx={{ pt: 5 }}>
                <Typography variant="subtitle2" color="error.main">
                  {t('cancelSubscriptionNote', {
                    expiredAt: DateTime.formatDate(expiredAt),
                  })}
                </Typography>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </OrganismFrame>
  )
}

export const UnregisteredInfoLabel = () => {
  const { t } = useTranslation('artistMembership')
  return (
    <Chip
      label={
        <Typography variant="overline" color="white" display="block">
          {t('unregisteredInfo')}
        </Typography>
      }
      sx={{
        height: 16,
        bgcolor: 'error.main',
        borderRadius: '2px',
        padding: '2px 4px',
        '.MuiChip-label': { px: 0 },
      }}
    />
  )
}

type PlanLabelProps = {
  interval?: MembershipInterval
  planRelationship?: `${MembershipPlanRelationship}`
}
export const PlanLabel: Organism<PlanLabelProps> = ({
  interval,
  planRelationship,
}) => {
  const { t } = useTranslation('artistMembership')

  const { label, bgcolor, color } = useMemo(() => {
    switch (planRelationship) {
      // ダウングレード先 翌月から入会
      case 'forJoinedLower':
      case 'forJoinedUpper':
      case 'carrier_forJoinedUpper':
      case 'carrier_forJoinedLower':
        return {
          label: t(`${interval || ''}JoinPlanLabel`),
          bgcolor: 'grey.200',
          color: 'text.secondary',
        }
      // ダウングレード元 期間まで入会中扱い
      case 'forLeave':
      case 'carrier_forLeave':
      case 'joined':
      case 'stopping':
      case 'konbini_waitingPayment':
      case 'konbini_joined':
        return {
          label: t(`${interval || ''}Joining`),
          bgcolor: 'primary.main',
          color: 'white',
        }
      // ダウングレード時のその他のプラン
      case 'notDowngradingUpper':
      case 'notDowngradingLower':
      case 'upper':
      case 'lower':
      case 'konbini_upper':
      case 'konbini_lower':
      case 'unjoined':
      default:
        return {
          label: t('non-member'),
          bgcolor: 'grey.50',
          color: 'text.hint',
        }
    }
  }, [planRelationship, t])

  if (!planRelationship) {
    return null
  }

  return (
    <Chip
      label={
        <Typography variant="overline" color={color} display="block">
          {label}
        </Typography>
      }
      sx={{
        height: 16,
        bgcolor,
        borderRadius: '2px',
        padding: '2px 4px',
        '.MuiChip-label': { px: 0 },
      }}
    />
  )
}
